import React from "react";
import CustomButton from "../../shared/CustomButton";
import { FilePicker } from "react-file-picker";
import UserInfo from "./UserInfo";
import UserAddressContainer from "../../shared/Address/AddressContainer";
import NewAddressForm from "../../shared/Address/NewAddressForm";
import UserPasswordEdit from "./UserPasswordEdit";
import UserConsents from "./UserConsents";
import Modal from "../../shared/Modal";
import {
  withContainers,
  ConsentsContainer,
  AddressContainer,
  Avatar,
  Image02,
  Lock,
  LogOut,
  AddPlus,
  UserService,
  AuthenticationService,
  CircleCheck,
  CircleWarningFilled,
  CloseMD,
  CircleCheckFilled,
} from "../../normalizer";
import { ToastService } from "../../shared/Toast";
import { t } from "../../../../i18n";
import { CampaignUserContainer } from "../../../containers/gamification2026";
import { withRouter } from "react-router-dom";

/* ── Feedback modal content: success ── */
const SuccessModalContent = ({ onClose }) => (
  <div className="password-feedback-modal">
    <div className="password-feedback-modal__header">
      <CircleCheckFilled className="password-feedback-modal__icon password-feedback-modal__icon--success" />
      <button type="button" className="password-feedback-modal__close" onClick={onClose} aria-label="Fechar">
        <CloseMD />
      </button>
    </div>
    <div className="password-feedback-modal__body">
      <h4 className="password-feedback-modal__title">Palavra-passe alterada</h4>
      <p className="password-feedback-modal__subtitle">A sua palavra-passe foi alterada com sucesso.</p>
    </div>
    <div className="password-feedback-modal__actions">
      <CustomButton onClick={onClose} className="password-feedback-modal__btn">Continuar</CustomButton>
    </div>
  </div>
);

/* ── Logout confirmation modal ── */
const LogoutConfirmModalContent = ({ onConfirm, onCancel }) => (
  <div className="logout-confirm-modal">
    <div className="logout-confirm-modal__header">
      <h4 className="logout-confirm-modal__title">Terminar sessão</h4>
      <button type="button" className="logout-confirm-modal__close" onClick={onCancel} aria-label="Fechar">
        <CloseMD />
      </button>
    </div>
    <div className="logout-confirm-modal__body">
      <p className="logout-confirm-modal__subtitle">Tem a certeza de que deseja terminar a sessão? Para voltar a aceder, terá de iniciar sessão novamente.</p>
    </div>
    <div className="logout-confirm-modal__actions">
      <CustomButton secondary onClick={onCancel} className="logout-confirm-modal__btn">Cancelar</CustomButton>
      <CustomButton onClick={onConfirm} className="logout-confirm-modal__btn">Terminar sessão</CustomButton>
    </div>
  </div>
);

/* ── Feedback modal content: failure ── */
const FailureModalContent = ({ errors, onClose }) => (
  <div className="password-feedback-modal password-feedback-modal--failure">
    <div className="password-feedback-modal__header">
      <CircleWarningFilled className="password-feedback-modal__icon password-feedback-modal__icon--failure" />
      <button type="button" className="password-feedback-modal__close" onClick={onClose} aria-label="Fechar">
        <CloseMD />
      </button>
    </div>
    <div className="password-feedback-modal__body">
      <h4 className="password-feedback-modal__title">Erro ao alterar palavra-passe</h4>
      {errors && errors.length > 0 ? (
        <ul className="password-feedback-modal__errors">
          {errors.map((err, i) => <li key={i} className="password-feedback-modal__error">{err}</li>)}
        </ul>
      ) : (
        <p className="password-feedback-modal__subtitle">Ocorreu um erro. Por favor tente novamente.</p>
      )}
    </div>
    <div className="password-feedback-modal__actions">
      <CustomButton secondary onClick={onClose} className="password-feedback-modal__btn">Fechar</CustomButton>
    </div>
  </div>
);

const UserDetails = (props) => {
  const {
    user,
    campaignUser,
    addresses,
    editingUserGeneralData,
    onEditGeneralData,
    isAdding,
    countries,
    states,
    selectedCountry,
    selectedState,
    onEditCancel,
    onEditSubmit,
    onNewAddressEnable,
    editPassword,
    onEditPassword,
    onEditPasswordCancel,
    onEditPasswordSubmit,
    onChangeGender,
    onAvatarChange,
    errors,
    consents,
    editConsents,
    onHandleEditConsents,
    onHandleCancelEditConsents,
    onUpdateConsent,
    updatedConsents,
    confirmEditConsents,
  } = props;

  const [feedbackModal, setFeedbackModal] = React.useState(null); // null | 'success' | 'failure'
  const [feedbackErrors, setFeedbackErrors] = React.useState([]);
  const [showLogoutConfirm, setShowLogoutConfirm] = React.useState(false);

  const handlePasswordSuccess = () => {
    setFeedbackModal('success');
  };

  const handlePasswordFailure = (errors) => {
    setFeedbackErrors(errors);
    setFeedbackModal('failure');
  };

  // Success: close feedback modal AND the outer password modal (no toast warning)
  const closeSuccessModal = () => {
    setFeedbackModal(null);
    onEditPasswordCancel && onEditPasswordCancel(false);
  };

  // Failure: just close the feedback — leave the form open so user can retry
  const closeFailureModal = () => setFeedbackModal(null);

  const { avatar: campaignAvatar = {} } = campaignUser;

  let avatar = undefined;
  if (campaignAvatar && campaignAvatar.url) {
    avatar = campaignAvatar.url;
  }

  const handleLogoutClick = () => setShowLogoutConfirm(true);

  const handleLogoutConfirm = () => {
    setShowLogoutConfirm(false);
    UserService.logOut().then(() => {
      AuthenticationService.logout();
      props.history.push(`/login`);
    });
  };

  const handleLogoutCancel = () => {
    setShowLogoutConfirm(false);
    ToastService.info('Operação cancelada.');
  };

  return (
    <>
      <div className="user-details">
        <div className="user-details-avatar">
          <div className="user-details-avatar-row">
            <div className="user-details-campaign-user">
              <img
                src={avatar || Avatar}
                alt="avatar"
                className="user-details-avatar-image"
              />
              <div className="user-details-campaign-user-name">
                <h4 className="user-details-campaign-user-name-text">
                  {user.first_name} {user.last_name}
                </h4>
                <div className="user-details-campaign-user-points">
                  {campaignUser.availablePoints} pontos
                </div>
              </div>
            </div>
            <div className="user-details-avatar-buttons">
              <FilePicker
                extensions={["jpg", "jpeg", "png"]}
                onChange={onAvatarChange}
                onError={() => onAvatarChange(null)}
              >
                <CustomButton
                  small
                  secondary
                  className="user-details-avatar-button"
                >
                  <Image02
                    width={20}
                    height={20}
                    className="user-details-avatar-button-icon"
                  />
                  {t("gamification2026.profile.change-image")}
                </CustomButton>
              </FilePicker>
              <CustomButton
                small
                secondary
                className="user-details-avatar-button"
                onClick={onEditPassword}
              >
                <Lock
                  width={20}
                  height={20}
                  className="user-details-avatar-button-icon"
                />
                {t("gamification2026.profile.change-password")}
              </CustomButton>
              <CustomButton
                small
                secondary
                className="user-details-avatar-button"
                onClick={handleLogoutClick}
              >
                <LogOut
                  width={20}
                  height={20}
                  className="user-details-avatar-button-icon"
                />
                {t("gamification2026.profile.logout")}
              </CustomButton>
            </div>
          </div>
        </div>
        <Modal show={editPassword} untoggle={onEditPasswordCancel} transparent>
          <div className="user-details-password">
            <UserPasswordEdit
              onEditPasswordCancel={onEditPasswordCancel}
              onEditPasswordSubmit={onEditPasswordSubmit}
              onPasswordSuccess={handlePasswordSuccess}
              onPasswordFailure={handlePasswordFailure}
            />
          </div>
        </Modal>

        <Modal show={showLogoutConfirm} untoggle={handleLogoutCancel}>
          <LogoutConfirmModalContent onConfirm={handleLogoutConfirm} onCancel={handleLogoutCancel} />
        </Modal>

        <Modal show={feedbackModal !== null} untoggle={feedbackModal === 'success' ? closeSuccessModal : closeFailureModal}>
          {feedbackModal === 'success' && <SuccessModalContent onClose={closeSuccessModal} />}
          {feedbackModal === 'failure' && <FailureModalContent errors={feedbackErrors} onClose={closeFailureModal} />}
        </Modal>
        <div className={`user-details-info ${editingUserGeneralData ? "user-details-info-edit" : ""}`}>
          <UserInfo
            editingUserGeneralData={editingUserGeneralData}
            onEditGeneralData={onEditGeneralData}
            onChangeGender={onChangeGender}
            onEditSubmit={onEditSubmit}
            onEditCancel={onEditCancel}
            {...user}
          />
        </div>
      </div>

      {consents.length > 0 && (
        <UserConsents
          editConsents={editConsents}
          consents={consents}
          onHandleEditConsents={onHandleEditConsents}
          onHandleCancelEditConsents={onHandleCancelEditConsents}
          onUpdateConsent={onUpdateConsent}
          updatedConsents={updatedConsents}
          confirmEditConsents={confirmEditConsents}
        />
      )}

      {addresses.map((address) => (
        <UserAddressContainer key={address.id} address={address} />
      ))}

      {isAdding ? (
        <NewAddressForm
          errors={errors}
          state={selectedState}
          country={selectedCountry}
          availableStates={states}
          availableCountries={countries}
          onAddressCreationCancel={props.onNewAddressCancel}
          onNewAddress={props.onNewAddressCreation}
          onCountryChange={props.onCountryChange}
          onStateChange={props.onStateChange}
        />
      ) : (
        <div className="user-details-add-address-action">
          <CustomButton secondary customSizing="medium body-s" onClick={onNewAddressEnable}>
            <AddPlus width={24} height={24} />
            {t("gamification2026.profile.add-address")}
          </CustomButton>
        </div>
      )}
    </>
  );
};

export default withContainers(withRouter(UserDetails), [
  CampaignUserContainer,
  AddressContainer,
  ConsentsContainer,
]);
